import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit2, FiTrash2 } from 'react-icons/fi';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import DataTable from '../components/DataTable';
import Modal from '../components/Modal';
import LoadingSpinner from '../components/LoadingSpinner';
import { categoriesAPI } from '../services/api';
import { toast } from 'react-toastify';
import ExportPDFButton from '../components/ExportPDFButton';

const CategoriesPage = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ nom: '', description: '' });
  const [submitting, setSubmitting] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, id: null });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const res = await categoriesAPI.getAll();
      setCategories(res.data.data || []);
    } catch (error) {
      toast.error('Erreur lors du chargement des catégories');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (category = null) => {
    if (category) {
      setEditingId(category.id);
      setFormData({ nom: category.nom, description: category.description || '' });
    } else {
      setEditingId(null);
      setFormData({ nom: '', description: '' });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (editingId) {
        await categoriesAPI.update(editingId, formData);
        toast.success('Catégorie mise à jour');
      } else {
        await categoriesAPI.create(formData);
        toast.success('Catégorie créée');
      }
      setShowModal(false);
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      await categoriesAPI.delete(deleteModal.id);
      toast.success('Catégorie supprimée');
      setDeleteModal({ isOpen: false, id: null });
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const columns = [
    { key: 'nom', label: 'Nom', sortable: true },
    { key: 'description', label: 'Description' },
  ];

  if (loading) {
    return (
      <div className="app-layout">
        <Sidebar />
        <div className="main-content">
          <Header />
          <div className="page-content" id="page-content">
            <LoadingSpinner />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-layout">
      <Sidebar />
      <div className="main-content">
        <Header />
        <div className="page-content" id="page-content">
          <div className="page-header">
            <h1 className="page-title">Catégories</h1>
            <ExportPDFButton title="OGP IMMO - Catégories" fileName="categories-ogp-immo" />
            <button onClick={() => handleOpenModal()} className="btn btn-secondary">
              <FiPlus /> Nouvelle
            </button>
          </div>

          <div className="card">
            <DataTable
              columns={columns}
              data={categories}
              loading={loading}
              actions={(row) => [
                <button
                  key="edit"
                  onClick={() => handleOpenModal(row)}
                  className="btn btn-secondary btn-sm"
                  title="Éditer"
                >
                  <FiEdit2 />
                </button>,
                <button
                  key="delete"
                  onClick={() => setDeleteModal({ isOpen: true, id: row.id })}
                  className="btn btn-error btn-sm"
                  title="Supprimer"
                >
                  <FiTrash2 />
                </button>,
              ]}
            />
          </div>

          {/* Modal */}
          <Modal
            isOpen={showModal}
            title={editingId ? 'Éditer la catégorie' : 'Nouvelle catégorie'}
            onClose={() => setShowModal(false)}
            footer={
              <>
                <button
                  onClick={() => setShowModal(false)}
                  className="btn btn-ghost"
                >
                  Annuler
                </button>
                <button onClick={handleSubmit} className="btn btn-primary" disabled={submitting}>
                  {submitting ? 'Sauvegarde...' : 'Sauvegarder'}
                </button>
              </>
            }
          >
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">Nom *</label>
                <input
                  type="text"
                  value={formData.nom}
                  onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                  required
                  placeholder="Nom de la catégorie"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Description"
                ></textarea>
              </div>
            </form>
          </Modal>

          {/* Delete Modal */}
          <Modal
            isOpen={deleteModal.isOpen}
            title="Confirmer la suppression"
            onClose={() => setDeleteModal({ isOpen: false, id: null })}
            footer={
              <>
                <button
                  onClick={() => setDeleteModal({ isOpen: false, id: null })}
                  className="btn btn-ghost"
                >
                  Annuler
                </button>
                <button onClick={handleDelete} className="btn btn-error">
                  Supprimer
                </button>
              </>
            }
          >
            <p>Êtes-vous sûr de vouloir supprimer cette catégorie?</p>
          </Modal>
        </div>
      </div>
    </div>
  );
};

export default CategoriesPage;
